// OS MOTORES DE CANVAS, LADO A LADO.
//
// Esta rota existe para que o item 4 do pedido seja VERIFICÁVEL e não apenas afirmado. O formato
// de documento declara quatro motores; aqui a mesma história é desenhada por cada um, com um
// seletor, para dar para ver a diferença em vez de ler sobre ela.
//
// O documento abaixo é de demonstração e está no código de propósito: ele exercita todos os
// tipos de elemento (quadro, balão, texto, forma, personagem) e todos os formatos de página.
// Quando a biblioteca tiver conteúdo real, esta rota passa a ler de lá — mas até então, uma
// demonstração honesta é melhor que uma tela vazia que não prova nada.

import { useState } from "react";
import Nav from "../../components/nav";
import { VisualizadorDePagina } from "../../motores";
import {
  DocumentoHQ,
  FORMATO_DOCUMENTO,
  VERSAO_DOCUMENTO,
  MotorCanvas,
  PADROES_PAGINA,
} from "../../api/documento.types";
import "./motores-demo.scss";

import { useTituloDaAba } from "../../hooks/use-titulo-da-aba";
/**
 * OS QUATRO MODOS DE MONTAR A PÁGINA — e o nome de cada um é o que ele FAZ.
 *
 * T8.16, na letra: *"A página pública 'Motores de desenho' fala em 'Grade CSS'…"* — e esta é uma
 * página PÚBLICA, aberta pelo rodapé por qualquer pessoa. "Grade CSS", "SVG" e "Konva (edição)" são
 * nomes de biblioteca e de tecnologia: dizem algo para quem escreve o código e nada para quem vai
 * usar.
 *
 * A REGRA DESTA CASA É EXPLÍCITA SOBRE ISSO: *"técnico sem jargão: diga o que a coisa faz em
 * palavras comuns, e dê o nome dela uma vez, para quem quiser procurar."* É o que está feito abaixo
 * — o `nome` diz para que serve, e o nome técnico aparece uma vez, no fim da explicação.
 *
 * O `id` NÃO mudou: ele é a chave do documento salvo (`PADROES_PAGINA` aponta para ele). Trocar o
 * `id` para "arrumar o nome" quebraria todo quadrinho já gravado — é a distinção entre o que a
 * pessoa lê e o que o dado guarda, a mesma dos ângulos de câmera.
 */
const MOTORES: { id: MotorCanvas; nome: string; paraQue: string }[] = [
  {
    id: "grade-css",
    nome: "Página para ler",
    paraQue:
      "O texto é texto de verdade: dá para copiar, o leitor de tela lê em voz alta, a busca " +
      "encontra e a impressão sai nítida. É o melhor modo para LER uma história. " +
      "(Quem for procurar: é montado com a grade do CSS.)",
  },
  {
    id: "fluxo-vertical",
    nome: "Rolagem no celular",
    paraQue:
      "Uma tira alta, um quadro por tela, lida rolando o dedo. É como criança lê quadrinho no " +
      "celular. (O nome de fora é webtoon.)",
  },
  {
    id: "svg",
    nome: "Arte para imprimir",
    paraQue:
      "Cresce para qualquer tamanho sem borrar, então serve para a capa e para levar à " +
      "impressora. (Quem for procurar: é desenho vetorial, em SVG.)",
  },
  {
    id: "konva",
    nome: "Mesa de edição",
    paraQue:
      "É onde se MEXE: arrastar, girar, mudar de tamanho, pôr um desenho na frente do outro. " +
      "Para apenas ler, a história volta para a página de leitura de propósito — esta mesa não " +
      "entrega texto que o leitor de tela alcance. (Quem for procurar: é a biblioteca Konva, " +
      "sobre canvas.)",
  },
];


function documentoDemo(): DocumentoHQ {
  const p = PADROES_PAGINA.pagina;
  return {
    formato: FORMATO_DOCUMENTO,
    versao: VERSAO_DOCUMENTO,
    id: "demo",
    titulo: "O mistério do rio",
    criadoEm: "2026-09-09T10:00:00.000Z",
    atualizadoEm: "2026-09-09T10:00:00.000Z",
    autor: { id: "demo", nome: "Turma 5º B", turma: "5B" },
    publicacao: { categoria: "didatico", estado: "rascunho" },
    paginas: [
      {
        id: "p1",
        formato: "pagina",
        motor: "grade-css",
        largura: p.largura,
        altura: p.altura,
        corFundo: "#F8F8F8",
        // A RETICULA ENTROU NA AMOSTRA (T2.108), e o lugar e este.
        //
        // Esta tela existe, na letra do comentario dela, "para que a afirmacao 'o formato suporta
        // quatro motores' seja VERIFICAVEL na tela". A reticula e capacidade nova do formato, e
        // capacidade que nao aparece na amostra e capacidade que ninguem consegue conferir — eu
        // mesmo tentei medi-la aqui e achei zero camada desenhada, porque a amostra nao a pedia.
        //
        // "media" e nao "forte": a amostra tem quatro quadros com cor de fundo e texto por cima,
        // e forte sobre isso mostraria a reticula e esconderia o que ela cobre.
        reticula: "media",
        camadas: [
          {
            id: "c1",
            nome: "Quadros",
            elementos: [
              { id: "q1", tipo: "quadro", x: 40, y: 40, largura: 560, altura: 400, corFundo: "#CFE8A8" },
              { id: "q2", tipo: "quadro", x: 632, y: 40, largura: 560, altura: 400, corFundo: "#BFDDF2" },
              { id: "q3", tipo: "quadro", x: 40, y: 472, largura: 1152, altura: 440, corFundo: "#F5DFC0" },
              { id: "q4", tipo: "quadro", x: 40, y: 944, largura: 1152, altura: 440, corFundo: "#E3D2F0" },
            ],
          },
          {
            id: "c2",
            nome: "Personagens",
            elementos: [
              { id: "pe1", tipo: "personagem", x: 380, y: 150, largura: 180, altura: 260, personagem: "pesquisadora" },
              { id: "pe2", tipo: "personagem", x: 980, y: 560, largura: 180, altura: 260, personagem: "professora" },
            ],
          },
          {
            id: "c3",
            nome: "Falas",
            elementos: [
              {
                id: "b1", tipo: "balao", x: 70, y: 70, largura: 280, altura: 90,
                texto: "O rio mudou de cor!", estilo: "grito", crescePorTexto: true,
              },
              {
                id: "b2", tipo: "balao", x: 664, y: 70, largura: 300, altura: 90,
                texto: "Será que foi a fábrica lá do alto do morro?", estilo: "pensamento",
                crescePorTexto: true,
              },
              {
                id: "b3", tipo: "balao", x: 72, y: 500, largura: 500, altura: 70,
                texto: "No dia seguinte, a turma voltou com um vidro para levar uma amostra da água.",
                estilo: "narracao", crescePorTexto: true,
              },
              {
                id: "b4", tipo: "balao", x: 90, y: 980, largura: 420, altura: 90,
                texto: "Vamos escrever uma carta para a prefeitura.", estilo: "fala",
                crescePorTexto: true,
              },
            ],
          },
        ],
      },
    ],
  };
}

/**
 * O NOME QUE A PESSOA LÊ, A PARTIR DA CHAVE QUE O DADO GUARDA.
 *
 * `PADROES_PAGINA` guarda o `id` (`"grade-css"`), porque é o que vai gravado no documento. A tela
 * mostrava esse `id` cru. As duas coisas continuam existindo — o que muda é qual delas se lê.
 */
function nomeDoModo(id: MotorCanvas) {
  return MOTORES.find((m) => m.id === id)?.nome ?? id;
}

function MotoresDemo() {
  // T8.39 · medido em producao em 26/09: esta rota dizia so "HQ-Lab".
  useTituloDaAba("Como a página é montada");
  const [motor, setMotor] = useState<MotorCanvas>("grade-css");
  const doc = documentoDemo();
  const escolhido = MOTORES.find((m) => m.id === motor)!;

  return (
    <div className="motores-container">
      <Nav />

      <header className="motores-topo hq-trama">
        <div className="motores-largura">
          {/* T8.37 · medido em 26/09 na pagina servida do build: "Grade CSS", "Konva" e "SVG"
              sairam do corpo em `fe6e22a`, e sobrou o TITULO dizendo "Motores de desenho" — a
              palavra que a queixa nomeia. O nome da aba ja era este, e o link do rodape ja dizia
              "Os formatos de pagina": o titulo era o unico lugar que ainda falava com programador. */}
          <h1>Como a página é montada</h1>
          <p>
            A mesma página, desenhada de quatro maneiras diferentes. Cada história escolhe a
            maneira que serve ao que ela é — e a escolha fica gravada no documento. Quem quiser
            procurar: cada uma dessas maneiras é um motor de desenho.
          </p>
        </div>
      </header>

      <main className="motores-conteudo motores-largura">
        <div className="motores-seletor" role="radiogroup" aria-label="Maneira de desenhar a página">
          {MOTORES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={motor === m.id}
              className={`motores-opcao ${motor === m.id ? "escolhido" : ""}`}
              onClick={() => setMotor(m.id)}
            >
              {m.nome}
            </button>
          ))}
        </div>

        <p className="motores-explicacao">{escolhido.paraQue}</p>

        <div className="motores-palco">
          <VisualizadorDePagina pagina={doc.paginas[0]} larguraDisponivel={860} forcarMotor={motor} />
        </div>

        <section className="motores-formatos">
          <h2>Formatos de página</h2>
          {/* T8.37 · aqui morava o pior pedaco da queixa, e `fe6e22a` nao chegou nele: esta secao
              dizia "motor padrao: grade-css" seis vezes — o `id` cru do dado, numa pagina publica.
              Medido em 26/09 na pagina servida do build: 9 ocorrencias da palavra "motor" na tela
              depois de o cabecalho ja estar consertado. Consertar o pedaco citado e deixar o vizinho
              e o formato de erro que este epico ja cometeu tres vezes. */}
          <p>
            O formato diz <em>o que a página é</em>; a maneira de desenhar diz{" "}
            <em>com que se desenha</em>. São separados de propósito: trocar a maneira de desenhar
            não reescreve o documento.
          </p>
          <dl>
            {Object.entries(PADROES_PAGINA).map(([id, p]) => (
              <div key={id}>
                <dt>{p.rotulo}</dt>
                <dd>
                  {p.explicacao}
                  <span className="motores-medida">
                    {p.largura} × {p.altura} · desenhada como: {nomeDoModo(p.motor)}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
    </div>
  );
}

export default MotoresDemo;
